import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getPlayerNameColor, buildPlayerEloTooltipText, formatSessionGamesCountLabel } from '../../utils/playerUtils';
import { isMobileSearchSheet, preventInputBlurOnPointerDown } from '../../utils/mobileSearchInput';
import { MobileSearchField, PlayerPickerSheet } from '../common';
import useKeyboardAwareScrollMaxHeight from '../../hooks/useKeyboardAwareScrollMaxHeight';
import { groupPlayersForPegBoardCourtAdd } from '../../utils/pegBoard';

function playerSearchText(player) {
  const fromParts = [player.first_name, player.last_name].filter(Boolean).join(' ').trim();
  if (fromParts) return fromParts.toLowerCase();
  return String(player.name || '').toLowerCase();
}

/**
 * Dropdown for adding players to empty court / upcoming slots.
 * Mobile renders a `PlayerPickerSheet`; desktop portaled centered panel (escapes court card transforms).
 * @param {Array} availablePlayers — active + on upcoming (ready to slot)
 * @param {Array} [waitingManagementPlayers] — optional signed_up / paused pool; competitive sessions pass [] so hosts must mark playing (waitlist) first
 * @param {Array} [occupiedPlayers] — on-court / upcoming players shown greyed out (not selectable)
 * @param {string} [title]
 * @param {string} [subtitle]
 */
const AddPlayerDropdown = ({
  isOpen,
  onClose,
  onSelectPlayer,
  availablePlayers = [],
  waitingManagementPlayers = [],
  sessionPlayerStatus = {},
  courtNumber,
  teamNumber,
  className = '',
  nextAssignments = [],
  onRemoveFromUpcoming,
  useSinglesElo = false,
  showEloTooltip = true,
  pegBoardQueue = null,
  occupiedPlayers = [],
  title = 'Add player',
  subtitle = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const desktopPanelRef = useRef(null);
  const searchAnchorRef = useRef(null);
  const [isMobileSheet] = useState(() => isMobileSearchSheet());
  const listMaxHeight = useKeyboardAwareScrollMaxHeight(searchAnchorRef, {
    enabled: isOpen && !isMobileSheet,
    maxPreferred: 360,
  });

  const { filteredPlaying, filteredWaiting, pegBoardGroups, filteredOccupied } = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const match = (player) => !q || playerSearchText(player).includes(q);
    const occupiedRows = (occupiedPlayers || [])
      .map((row) => (row?.player ? row : { player: row, location: 'On court' }))
      .filter((row) => row.player && match(row.player));
    if (pegBoardQueue?.waitlistIds) {
      const grouped = groupPlayersForPegBoardCourtAdd({
        waitlist: pegBoardQueue.waitlistIds,
        selectionWindow: pegBoardQueue.selectionWindow,
        availablePlayers,
      });
      return {
        filteredPlaying: [],
        filteredWaiting: waitingManagementPlayers.filter(match),
        pegBoardGroups: {
          windowPlayers: grouped.windowPlayers.filter((row) => match(row.player)),
          restPlayers: grouped.restPlayers.filter((row) => match(row.player)),
          extraPlayers: grouped.extraPlayers.filter(match),
          windowSize: grouped.windowSize,
        },
        filteredOccupied: occupiedRows,
      };
    }
    return {
      filteredPlaying: availablePlayers.filter(match),
      filteredWaiting: waitingManagementPlayers.filter(match),
      pegBoardGroups: null,
      filteredOccupied: occupiedRows,
    };
  }, [searchTerm, availablePlayers, waitingManagementPlayers, pegBoardQueue, occupiedPlayers]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Desktop only — mobile users tap the search field when ready (avoids keyboard jank on open);
  // PlayerPickerSheet handles its own focus/scroll-lock lifecycle for the mobile sheet.
  useEffect(() => {
    if (!isOpen || isMobileSheet || !searchInputRef.current) return;
    const t = setTimeout(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    }, 100);
    return () => clearTimeout(t);
  }, [isOpen, isMobileSheet]);

  // Desktop escape / click-outside — PlayerPickerSheet handles this itself on mobile.
  useEffect(() => {
    if (isMobileSheet) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isMobileSheet, onClose]);

  useEffect(() => {
    if (isMobileSheet) return;
    const handleClickOutside = (event) => {
      if (desktopPanelRef.current && !desktopPanelRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
      return () => document.removeEventListener('pointerdown', handleClickOutside);
    }
  }, [isOpen, isMobileSheet, onClose]);

  if (!isOpen) return null;

  const findUpcomingPlacement = (playerId) => {
    if (!nextAssignments?.length) return null;
    for (let idx = 0; idx < nextAssignments.length; idx++) {
      const m = nextAssignments[idx];
      if (m.team1?.includes(playerId)) return { matchIndex: idx, team: 1 };
      if (m.team2?.includes(playerId)) return { matchIndex: idx, team: 2 };
    }
    return null;
  };

  const isPlayerInUpcoming = (playerId) => findUpcomingPlacement(playerId) != null;

  const handlePlayerSelect = (player) => {
    const place = findUpcomingPlacement(player.id);
    if (place && onRemoveFromUpcoming) {
      onRemoveFromUpcoming(player.id, place.team, place.matchIndex);
    }
    if (onSelectPlayer) {
      onSelectPlayer(player.id, courtNumber, teamNumber);
    }
    onClose();
  };

  const renderRow = (player, { waitingBadge, queuePosition, isPicker } = {}) => {
    const rawName = [player.first_name, player.last_name].filter(Boolean).join(' ').trim() || player.name || '';
    const nameFormat = formatNameForDisplay(rawName, { maxLength: 20, useInitials: false });
    const inUpcoming = isPlayerInUpcoming(player.id);
    const gamesLabel = formatSessionGamesCountLabel(player);

    return (
      <button
        key={`${player.id}-${waitingBadge || queuePosition || 'playing'}`}
        type="button"
        onMouseDown={preventInputBlurOnPointerDown}
        onClick={() => handlePlayerSelect(player)}
        className={`w-full p-2 min-h-[44px] touch-manipulation text-left rounded transition-colors group ${
          inUpcoming ? 'bg-warning-light/20 hover:bg-warning-light/30' : 'bg-surface hover:bg-surface-subtle'
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {queuePosition != null && (
              <span
                className="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-text-muted"
                title={`Queue position ${queuePosition}`}
              >
                {queuePosition}
              </span>
            )}
            {inUpcoming && (
              <span className="text-orange-500 text-xs font-bold shrink-0" title="On next on court">
                ⏳
              </span>
            )}
            {waitingBadge && (
              <span
                className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-warning-light/50 text-warning-dark"
                title="From player management queue — will mark as playing"
              >
                {waitingBadge}
              </span>
            )}
            {isPicker && (
              <span
                className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-info-light text-info-dark"
                title="Next player to pick a game"
              >
                Picker
              </span>
            )}
            <span
              className={`font-medium text-base truncate ${showEloTooltip ? 'cursor-help' : ''} ${getPlayerNameColor(player.gender)}`}
              title={showEloTooltip ? buildPlayerEloTooltipText(player, { useSinglesElo }) : undefined}
            >
              {nameFormat.display}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-sm text-text-muted">
            <span title="Games this session">{gamesLabel}</span>
          </div>
        </div>
      </button>
    );
  };

  const renderOccupiedRow = (row) => {
    const player = row.player;
    if (!player) return null;
    const rawName = [player.first_name, player.last_name].filter(Boolean).join(' ').trim() || player.name || '';
    const nameFormat = formatNameForDisplay(rawName, { maxLength: 20, useInitials: false });
    const location = row.location || 'On court';
    return (
      <div
        key={`occupied-${player.id}`}
        className="flex w-full min-h-[44px] items-center justify-between gap-2 rounded p-2 opacity-50"
        aria-disabled="true"
        title={`${nameFormat.display} is on ${location}`}
      >
        <span className={`min-w-0 truncate font-medium text-base ${getPlayerNameColor(player.gender)}`}>
          {nameFormat.display}
        </span>
        <span className="shrink-0 text-xs text-text-muted">{location}</span>
      </div>
    );
  };

  const pegBoardHasAny =
    pegBoardGroups &&
    (pegBoardGroups.windowPlayers.length > 0 ||
      pegBoardGroups.restPlayers.length > 0 ||
      pegBoardGroups.extraPlayers.length > 0 ||
      filteredWaiting.length > 0 ||
      filteredOccupied.length > 0);
  const hasAny = pegBoardGroups
    ? pegBoardHasAny
    : filteredPlaying.length > 0 || filteredWaiting.length > 0 || filteredOccupied.length > 0;
  const emptyMessage = searchTerm.trim() ? 'No players found' : 'No available players';

  const sectionHeading = (heading, hint) => (
    <div className="mb-1.5 px-0.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">{heading}</p>
      {hint ? <p className="text-[10px] text-text-muted leading-snug">{hint}</p> : null}
    </div>
  );

  const occupiedSection =
    filteredOccupied.length > 0 ? (
      <div className="border-t border-border-medium pt-3">
        {sectionHeading('On court', 'Already playing or next up — not available to pick')}
        <div className="space-y-1">{filteredOccupied.map(renderOccupiedRow)}</div>
      </div>
    ) : null;

  const resultSections = pegBoardGroups ? (
    <div className="space-y-3">
      {pegBoardGroups.windowPlayers.length > 0 && (
        <div>
          {sectionHeading(
            'Picker window',
            `Next ${pegBoardGroups.windowSize} in the queue — includes the picker`
          )}
          <div className="space-y-1">
            {pegBoardGroups.windowPlayers.map((row) =>
              renderRow(row.player, { queuePosition: row.queuePosition, isPicker: row.isPicker })
            )}
          </div>
        </div>
      )}
      {pegBoardGroups.restPlayers.length > 0 && (
        <div className="border-t border-border-medium pt-3">
          {sectionHeading('Rest of queue', 'Further back in the queue')}
          <div className="space-y-1">
            {pegBoardGroups.restPlayers.map((row) =>
              renderRow(row.player, { queuePosition: row.queuePosition })
            )}
          </div>
        </div>
      )}
      {pegBoardGroups.extraPlayers.length > 0 && (
        <div className="border-t border-border-medium pt-3">
          {sectionHeading('On next court', 'Already queued — adding moves them onto this court')}
          <div className="space-y-1">{pegBoardGroups.extraPlayers.map((p) => renderRow(p))}</div>
        </div>
      )}
      {filteredWaiting.length > 0 && (
        <div className="border-t border-border-medium pt-3">
          {sectionHeading('Signed up or paused', 'Not marked playing yet — adding them sets status to playing.')}
          <div className="space-y-1">
            {filteredWaiting.map((p) => {
              const st = sessionPlayerStatus[p.id];
              const badge = st === 'paused' ? 'Paused' : 'Signed up';
              return renderRow(p, { waitingBadge: badge });
            })}
          </div>
        </div>
      )}
      {occupiedSection}
    </div>
  ) : (
    <div className="space-y-3">
      {filteredPlaying.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5 px-0.5">
            Playing / on upcoming
          </p>
          <div className="space-y-1">{filteredPlaying.map((p) => renderRow(p))}</div>
        </div>
      )}
      {filteredWaiting.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5 px-0.5">
            Signed up or paused
          </p>
          <p className="text-[10px] text-text-muted mb-1.5 px-0.5 leading-snug">
            Not marked playing yet — adding them sets status to playing.
          </p>
          <div className="space-y-1">
            {filteredWaiting.map((p) => {
              const st = sessionPlayerStatus[p.id];
              const badge = st === 'paused' ? 'Paused' : 'Signed up';
              return renderRow(p, { waitingBadge: badge });
            })}
          </div>
        </div>
      )}
      {occupiedSection}
    </div>
  );

  const resolvedSubtitle =
    subtitle ||
    (courtNumber != null && teamNumber != null ? `Court ${courtNumber}, team ${teamNumber}` : null);

  if (isMobileSheet) {
    return (
      <PlayerPickerSheet
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        subtitle={resolvedSubtitle}
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search players…"
        hasResults={hasAny}
        emptyMessage={emptyMessage}
        className={`z-[80] ${className}`}
      >
        {resultSections}
      </PlayerPickerSheet>
    );
  }

  return createPortal(
    <div className={`fixed inset-0 z-[80] ${className}`}>
      <div className="absolute inset-0 bg-black/20" onClick={onClose} aria-hidden />

      <div
        ref={desktopPanelRef}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 max-h-96 flex flex-col bg-surface shadow-xl rounded-xl border border-border-medium"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-player-title"
      >
        <div className="flex items-center justify-between p-3 border-b border-border shrink-0">
          <div>
            <h3 id="add-player-title" className="text-sm font-semibold text-text-primary">{title}</h3>
            {resolvedSubtitle ? (
              <p className="text-xs text-text-secondary">{resolvedSubtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors p-2 min-h-[44px] min-w-[44px] touch-manipulation"
            aria-label="Close"
          >
            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div ref={searchAnchorRef} className="p-3 pb-2 shrink-0">
          <MobileSearchField
            inputRef={searchInputRef}
            showIcon
            placeholder="Search players…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input w-full py-2 pr-3 border-border shadow-brand-sm transition-[border-color,box-shadow] duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-3 touch-pan-y"
          style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
        >
          {hasAny ? (
            resultSections
          ) : (
            <div className="text-center py-6">
              <p className="text-text-muted text-xs">{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddPlayerDropdown;
